import { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  Modal,
  TextInput,
  Image,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  Dimensions,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  withDelay,
  SlideInDown,
  SlideOutDown,
  FadeIn,
  FadeOut,
  runOnJS,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { MessageSquarePlus, X, Camera, Send, Bug, Lightbulb, Layout, MoreHorizontal } from 'lucide-react-native';
import { usePathname } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThemeColors } from '@/lib/useThemeColors';
import { useIsAuthenticated } from '@/lib/supabase/auth';
import { useAlert } from '@/components/AlertDialog';
import { hapticMedium, hapticSuccess, hapticLight } from '@/lib/haptics';
import { isWeb } from '@/lib/platform';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { cn } from '@/lib/cn';
import { useFeedback, captureScreenshot } from '@/lib/feedback';
import { track } from '@/lib/analytics';
import type { FeedbackCategory } from '@/lib/feedback';

const AUTH_SCREENS = ['/login', '/forgot-password', '/reset-password', '/mfa-verify'];

const CATEGORIES: { key: FeedbackCategory; label: string; icon: typeof Bug }[] = [
  { key: 'bug', label: 'Bug', icon: Bug },
  { key: 'feature', label: 'Feature', icon: Lightbulb },
  { key: 'ux', label: 'UX', icon: Layout },
  { key: 'other', label: 'Other', icon: MoreHorizontal },
];

const BANNER_HEIGHT = 48;
const HANDLE_HEIGHT = 20;

export default function FeedbackButton() {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const { isAuthenticated, isLoading } = useIsAuthenticated();
  const pathname = usePathname();
  const alert = useAlert();
  const mutation = useFeedback();

  const [modalVisible, setModalVisible] = useState(false);
  const [screenshotUri, setScreenshotUri] = useState<string | null>(null);
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [text, setText] = useState('');
  const [previewFull, setPreviewFull] = useState(false);

  const inputRef = useRef<TextInput>(null);
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Slide-to-reveal animation: 0 = hidden, 1 = revealed
  const revealProgress = useSharedValue(0);

  const clearAutoHide = () => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
  };

  const startAutoHide = useCallback(() => {
    clearAutoHide();
    autoHideTimer.current = setTimeout(() => {
      revealProgress.value = withSpring(0, { damping: 20, stiffness: 200 });
    }, 4000);
  }, []);

  const showBanner = useCallback(() => {
    revealProgress.value = withSpring(1, { damping: 18, stiffness: 220 });
    startAutoHide();
  }, [startAutoHide]);

  const hideBanner = useCallback(() => {
    clearAutoHide();
    revealProgress.value = withSpring(0, { damping: 20, stiffness: 200 });
  }, []);

  // Pan gesture on the handle area
  const panGesture = Gesture.Pan()
    .onUpdate((e) => {
      // Normalize: positive translationY = pulling down = reveal
      const progress = Math.min(Math.max(e.translationY / BANNER_HEIGHT, 0), 1);
      revealProgress.value = progress;
    })
    .onEnd((e) => {
      if (e.translationY > 20) {
        // Reveal
        revealProgress.value = withSpring(1, { damping: 18, stiffness: 220 });
        runOnJS(startAutoHide)();
      } else {
        // Snap back
        revealProgress.value = withSpring(0, { damping: 20, stiffness: 200 });
      }
    });

  // Swipe up gesture on the revealed banner to dismiss
  const dismissGesture = Gesture.Pan()
    .onEnd((e) => {
      if (e.translationY < -15) {
        revealProgress.value = withSpring(0, { damping: 20, stiffness: 200 });
        runOnJS(clearAutoHide)();
      }
    });

  const handleBarStyle = useAnimatedStyle(() => ({
    opacity: 1 - revealProgress.value * 0.3,
  }));

  const bannerStyle = useAnimatedStyle(() => {
    const translateY = (1 - revealProgress.value) * -BANNER_HEIGHT;
    return {
      transform: [{ translateY }],
      opacity: revealProgress.value,
    };
  });

  // Web keyboard shortcut: Cmd+Shift+F / Ctrl+Shift+F
  useEffect(() => {
    if (!isWeb) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'f') {
        e.preventDefault();
        handleOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isAuthenticated, modalVisible]);

  const handleOpen = useCallback(async () => {
    if (!isAuthenticated || modalVisible) return;
    hapticMedium();
    hideBanner();

    // Capture BEFORE opening modal so the modal isn't in the screenshot
    const uri = await captureScreenshot();
    setScreenshotUri(uri);
    setModalVisible(true);
  }, [isAuthenticated, modalVisible, hideBanner]);

  const handleClose = () => {
    setModalVisible(false);
    setScreenshotUri(null);
    setCategory('bug');
    setText('');
    setPreviewFull(false);
  };

  const handleSubmit = async () => {
    if (!text.trim()) return;

    try {
      await mutation.mutateAsync({
        text: text.trim(),
        category,
        screenshotUri,
      });
      hapticSuccess();
      track('feedback_submitted');
      handleClose();
      alert.success('Thanks for your feedback!', 'We appreciate you helping us improve Inverio.');
    } catch {
      alert.error('Failed to send feedback', 'Please try again later.');
    }
  };

  // Hide conditions
  if (isLoading || !isAuthenticated) return null;
  if (AUTH_SCREENS.some((s) => pathname.startsWith(s))) return null;

  const topOffset = insets.top;

  return (
    <>
      {/* Pull-down handle + banner area — native only (gestures block header clicks on web) */}
      {!isWeb && (
        <View
          style={{
            position: 'absolute',
            top: topOffset,
            left: 0,
            right: 0,
            zIndex: 999,
            alignItems: 'center',
            pointerEvents: 'box-none',
          }}
        >
          {/* Handle bar - always visible */}
          <GestureDetector gesture={panGesture}>
            <Animated.View
              style={[
                handleBarStyle,
                {
                  width: 40,
                  height: HANDLE_HEIGHT,
                  justifyContent: 'center',
                  alignItems: 'center',
                },
              ]}
            >
              <View
                style={{
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: colors.text.tertiary,
                  opacity: 0.25,
                }}
              />
            </Animated.View>
          </GestureDetector>

          {/* Revealed feedback banner */}
          <GestureDetector gesture={dismissGesture}>
            <Animated.View
              style={[
                bannerStyle,
                {
                  height: BANNER_HEIGHT,
                  overflow: 'hidden',
                },
              ]}
            >
              <Pressable
                onPress={handleOpen}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 16,
                  paddingVertical: 10,
                  borderRadius: 24,
                  backgroundColor: colors.primary[600],
                  shadowColor: colors.primary[700],
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.3,
                  shadowRadius: 6,
                  elevation: 4,
                }}
              >
                <MessageSquarePlus size={16} color="#fff" />
                <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Beta Feedback</Text>
              </Pressable>
            </Animated.View>
          </GestureDetector>
        </View>
      )}

      {/* Modal */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="none"
        onRequestClose={handleClose}
        statusBarTranslucent
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
          {/* Backdrop */}
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(150)}
            style={{
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              justifyContent: isWeb ? 'center' : 'flex-end',
              alignItems: isWeb ? 'center' : 'stretch',
            }}
          >
            <Pressable style={{ flex: 1 }} onPress={handleClose} />

            {/* Sheet */}
            <Animated.View
              entering={SlideInDown.duration(350).springify().damping(18)}
              exiting={SlideOutDown.duration(250)}
              style={{
                backgroundColor: colors.surface.primary,
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                ...(isWeb && { borderBottomLeftRadius: 24, borderBottomRightRadius: 24, width: '100%', maxWidth: 480 }),
                paddingBottom: Platform.OS === 'ios' ? 34 : 24,
                maxHeight: Dimensions.get('window').height * 0.85,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: -4 },
                shadowOpacity: 0.15,
                shadowRadius: 16,
                elevation: 12,
              }}
            >
              {/* Handle bar */}
              <View className="items-center pt-3 pb-1">
                <View
                  className="w-10 h-1 rounded-full"
                  style={{ backgroundColor: colors.border.medium }}
                />
              </View>

              {/* Keyboard-aware: the feedback body is a multiline field near the
                  bottom of the sheet — the single field most likely to be
                  covered. Props preserved verbatim; only the container differs. */}
              <KeyboardAwareBody
                bounces={false}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                style={isWeb ? { maxHeight: Dimensions.get('window').height * 0.7 } : undefined}
              >
                {/* Header */}
                <View className="flex-row items-center justify-between px-5 pt-2 pb-4">
                  <Text
                    className="text-lg font-bold"
                    style={{ color: colors.text.primary }}
                  >
                    Send Feedback
                  </Text>
                  <Pressable
                    onPress={handleClose}
                    className="w-8 h-8 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.surface.tertiary }}
                  >
                    <X size={16} color={colors.text.secondary} />
                  </Pressable>
                </View>

                {/* Screenshot preview */}
                {screenshotUri && (
                  <Pressable
                    onPress={() => setPreviewFull(!previewFull)}
                    className="mx-5 mb-4 rounded-xl overflow-hidden"
                    style={{
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  >
                    {previewFull ? (
                      <Image
                        source={{ uri: screenshotUri }}
                        style={{ width: '100%', height: 300 }}
                        resizeMode="contain"
                      />
                    ) : (
                      <View className="flex-row items-center gap-3 p-3">
                        <Image
                          source={{ uri: screenshotUri }}
                          style={{ width: 48, height: 48, borderRadius: 8 }}
                          resizeMode="cover"
                        />
                        <View className="flex-1">
                          <Text
                            className="text-xs font-medium"
                            style={{ color: colors.text.secondary }}
                          >
                            Screenshot attached
                          </Text>
                          <Text
                            className="text-xs"
                            style={{ color: colors.text.tertiary }}
                          >
                            Tap to preview
                          </Text>
                        </View>
                        <Camera size={16} color={colors.text.tertiary} />
                      </View>
                    )}
                  </Pressable>
                )}

                {/* Category pills */}
                <View className="px-5 mb-4">
                  <Text
                    className="text-xs font-semibold uppercase tracking-wider mb-2"
                    style={{ color: colors.text.tertiary }}
                  >
                    Category
                  </Text>
                  <View className="flex-row gap-2">
                    {CATEGORIES.map(({ key, label, icon: Icon }) => {
                      const active = category === key;
                      return (
                        <Pressable
                          key={key}
                          onPress={() => {
                            hapticLight();
                            setCategory(key);
                          }}
                          className={cn(
                            'flex-row items-center gap-1.5 px-3.5 py-2 rounded-full',
                          )}
                          style={{
                            backgroundColor: active ? colors.primary[500] : colors.surface.tertiary,
                          }}
                        >
                          <Icon
                            size={14}
                            color={active ? '#FFFFFF' : colors.text.secondary}
                          />
                          <Text
                            className="text-sm font-medium"
                            style={{
                              color: active ? '#FFFFFF' : colors.text.secondary,
                            }}
                          >
                            {label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>

                {/* Text input */}
                <View className="px-5 mb-5">
                  <TextInput
                    ref={inputRef}
                    value={text}
                    onChangeText={setText}
                    placeholder="Tell us what's on your mind..."
                    placeholderTextColor={colors.text.tertiary}
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                    className="rounded-xl p-4 text-base"
                    style={{
                      backgroundColor: colors.surface.secondary,
                      color: colors.text.primary,
                      minHeight: 120,
                      borderWidth: 1,
                      borderColor: colors.border.light,
                    }}
                  />
                </View>

                {/* Actions */}
                <View className="flex-row gap-3 px-5 pb-2">
                  <Pressable
                    onPress={handleClose}
                    className="flex-1 py-3.5 rounded-xl items-center"
                    style={{ backgroundColor: colors.surface.tertiary }}
                  >
                    <Text
                      className="font-semibold text-base"
                      style={{ color: colors.text.secondary }}
                    >
                      Cancel
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={handleSubmit}
                    disabled={!text.trim() || mutation.isPending}
                    className="flex-1 flex-row gap-2 py-3.5 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor: text.trim()
                        ? colors.primary[500]
                        : colors.primary[200],
                      opacity: mutation.isPending ? 0.7 : 1,
                    }}
                  >
                    <Send size={16} color="#FFFFFF" />
                    <Text className="font-semibold text-base text-white">
                      {mutation.isPending ? 'Sending...' : 'Send'}
                    </Text>
                  </Pressable>
                </View>
              </KeyboardAwareBody>
            </Animated.View>
          </Animated.View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}
